import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { site } from "@/data/site";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setMenuOpen(false);
    scrollTo(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        scrolled || menuOpen
          ? "border-white/10 bg-[#08080A]/85 backdrop-blur-xl"
          : "border-transparent bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
        <button
          onClick={() => { setMenuOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          className="font-display text-sm font-black uppercase tracking-tight text-white"
          data-testid="nav-logo"
        >
          CHRIS<span className="text-purple">JST</span>
          <span className="text-gold">PERFORMANCE</span>
        </button>

        <nav className="hidden items-center gap-8 md:flex">
          {site.nav.map((item) => (
            <button
              key={item.href}
              onClick={() => scrollTo(item.href)}
              className="text-xs font-bold uppercase tracking-[0.2em] text-white/60 transition-colors duration-200 hover:text-gold"
              data-testid={`nav-link-${item.label.toLowerCase()}`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={site.igUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="clip-tag bg-gold px-5 py-2 text-xs font-black uppercase tracking-[0.15em] text-black transition-transform duration-200 hover:-translate-y-0.5"
            data-testid="nav-cta-button"
          >
            Start Training
          </a>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="border-2 border-white/25 p-2 text-white transition-colors duration-200 hover:border-purple hover:text-purple md:hidden"
            data-testid="nav-mobile-menu-toggle"
            aria-expanded={menuOpen}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-[#08080A]/95 backdrop-blur-xl md:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col px-6 py-4">
              {site.nav.map((item) => (
                <button
                  key={item.href}
                  onClick={() => go(item.href)}
                  className="border-b border-white/10 py-4 text-left font-display text-sm font-black uppercase tracking-[0.15em] text-white transition-colors duration-200 last:border-0 hover:text-gold"
                  data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
